import React, { useState } from 'react';
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import {
    Layout,
    Menu,
    Button,
    Avatar,
    Dropdown,
    List,
    Typography,
    Space,
    Input,
    Modal,
    Select
} from 'antd';
import type { MenuProps } from 'antd';
import {
    PlusOutlined,
    UserOutlined,
    LogoutOutlined,
    SettingOutlined,
    DashboardOutlined,
    FileTextOutlined,
    CodeOutlined,
    SearchOutlined,
    HomeOutlined,
    DownOutlined,
    FolderOutlined,
    ThunderboltOutlined,
    MailOutlined,
    TableOutlined,
    DatabaseOutlined,
    ApiOutlined,
    SafetyCertificateOutlined,
    RobotOutlined,
    SmileOutlined,
    DeploymentUnitOutlined,
    MenuOutlined,
} from '@ant-design/icons';
import '../styles/HomePage.css'; // Reuse existing styles

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

interface Collection {
    id: string;
    name: string;
    type: 'dashboard' | 'report' | 'query';
    lastAccessed: string;
    starred: boolean;
}

const mockCollections: Collection[] = [
    { id: '1', name: 'Our analytics', type: 'dashboard', lastAccessed: '2025-05-27', starred: true },
    { id: '2', name: 'Your personal collection', type: 'report', lastAccessed: '2025-05-26', starred: true },
    { id: '3', name: '3PL Analytics', type: 'dashboard', lastAccessed: '2025-05-25', starred: false },
    { id: '4', name: '[Prod] Finance Support', type: 'query', lastAccessed: '2025-05-24', starred: false },
];

const Grid3x3Icon = () => (
    <svg width="1em" height="1em" viewBox="0 0 21 21" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <rect x="1" y="1" width="5" height="5" rx="1" />
        <rect x="8" y="1" width="5" height="5" rx="1" />
        <rect x="15" y="1" width="5" height="5" rx="1" />
        <rect x="1" y="8" width="5" height="5" rx="1" />
        <rect x="8" y="8" width="5" height="5" rx="1" />
        <rect x="15" y="8" width="5" height="5" rx="1" />
        <rect x="1" y="15" width="5" height="5" rx="1" />
        <rect x="8" y="15" width="5" height="5" rx="1" />
        <rect x="15" y="15" width="5" height="5" rx="1" />
    </svg>
);

const MainLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { workspaceId } = useParams<{ workspaceId: string }>();

    const handleLogout = () => {
        localStorage.removeItem('isAuthenticated');
        navigate('/');
    };

    const userItems: MenuProps['items'] = [
        { key: 'profile', icon: <UserOutlined />, label: 'Profile' },
        { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
        { type: 'divider' },
        { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: handleLogout },
    ];

    const appsItems: MenuProps['items'] = [
        {
            key: 'bi',
            label: (
                <div className="app-launcher-item">
                    <div className="app-icon-wrapper" style={{ background: '#bae7ff', color: '#1890ff' }}>
                        <DashboardOutlined style={{ fontSize: 20 }} />
                    </div>
                    <div className="app-details">
                        <div className="app-title">Business Intelligence</div>
                        <div className="app-desc">Analytics & Reporting</div>
                    </div>
                </div>
            )
        },
        {
            key: 'etl',
            label: (
                <div className="app-launcher-item">
                    <div className="app-icon-wrapper" style={{ background: '#d9f7be', color: '#52c41a' }}>
                        <DeploymentUnitOutlined style={{ fontSize: 20 }} />
                    </div>
                    <div className="app-details">
                        <div className="app-title">ETL Pipeline</div>
                        <div className="app-desc">Data Integration</div>
                    </div>
                </div>
            )
        },
        {
            key: 'ai-control',
            label: (
                <div className="app-launcher-item">
                    <div className="app-icon-wrapper" style={{ background: '#efdbff', color: '#722ed1' }}>
                        <RobotOutlined style={{ fontSize: 20 }} />
                    </div>
                    <div className="app-details">
                        <div className="app-title">AI Control Tower</div>
                        <div className="app-desc">GenAI & Automation</div>
                    </div>
                </div>
            )
        },
        {
            key: 'iron-audit',
            label: (
                <div className="app-launcher-item">
                    <div className="app-icon-wrapper" style={{ background: '#fff2e8', color: '#fa541c' }}>
                        <SafetyCertificateOutlined style={{ fontSize: 20 }} />
                    </div>
                    <div className="app-details">
                        <div className="app-title">Iron Audit</div>
                        <div className="app-desc">Security & Compliance</div>
                    </div>
                </div>
            )
        },
    ];

    // Collapsed state for Sider
    const [collapsed, setCollapsed] = useState(false);
    
    const newItemItems: MenuProps['items'] = [
        { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
        { 
            key: 'report', 
            icon: <FileTextOutlined />, 
            label: 'Report', 
            onClick: () => navigate(`/${workspaceId}/visualise/report`) 
        },
        { key: 'query', icon: <CodeOutlined />, label: 'Native Query' },
    ];

    // State for sidebar sections collapse
    const [sectionState, setSectionState] = useState({
        collections: true,
        schedulers: true,
        embeddedBi: true,
        auditLogs: true,
        admin: true,
    });

    const toggleSection = (section: keyof typeof sectionState) => {
        setSectionState(prev => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    // Determine current active section for styling based on URL
    const isHome = location.pathname.endsWith('/home');
    // Simple check for now, can be expanded

    const workspaceName = workspaceId ? workspaceId.charAt(0).toUpperCase() + workspaceId.slice(1) : 'Workspace';
    const workspaces = ['Shipsy', 'Netflix', 'Amazon', 'Uber'];

    const workspaceItems: MenuProps['items'] = workspaces.map(ws => ({
        key: ws.toLowerCase(),
        label: ws,
        onClick: () => {
            navigate(`/${ws.toLowerCase()}/home`);
        }
    }));

    return (
        <Layout className="home-layout" style={{ minHeight: '100vh' }}>
            {/* Header with Search */}
            <Header className="home-header">
                <div className="header-left">
                    <div className="app-logo">
                        {/* App Launcher with 3x3 Grid */}
                        <Dropdown menu={{ items: appsItems }} trigger={['click']} overlayClassName="app-launcher-dropdown">
                            <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.1)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, marginRight: 12, cursor: 'pointer' }}>
                                <Grid3x3Icon />
                            </div>
                        </Dropdown>
                        <Text style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>
                            <span style={{ fontWeight: 700 }}>QT</span>
                            <span style={{ opacity: 0.5, margin: '0 8px' }}>|</span>
                            <Dropdown menu={{ items: workspaceItems }} trigger={['click']}>
                                <span style={{ fontWeight: 600, cursor: 'pointer', borderBottom: '1px dashed rgba(255,255,255,0.4)', paddingBottom: 2 }}>
                                    {workspaceName} <DownOutlined style={{ fontSize: 10, verticalAlign: 'middle', marginLeft: 4 }} />
                                </span>
                            </Dropdown>
                            <span style={{ opacity: 0.5, margin: '0 8px' }}>|</span>
                            <span style={{ fontWeight: 400, opacity: 0.9 }}>Business Intelligence</span>
                        </Text>
                    </div>
                </div>

                <div className="header-right">
                    <div className="header-search">
                        <Input
                            prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.4)' }} />}
                            placeholder="Search..."
                            className="custom-search-input dark-theme-search"
                            bordered={false}
                        />
                    </div>

                    <Space size={16}>
                        <Dropdown menu={{ items: newItemItems }} trigger={['click']} overlayClassName="new-item-dropdown">
                            <Button type="primary" icon={<PlusOutlined />} className="new-button">
                                New
                            </Button>
                        </Dropdown>
                        <Button type="text" icon={<SettingOutlined style={{ fontSize: '18px', color: 'rgba(255,255,255,0.65)' }} />} />
                        <Dropdown menu={{ items: userItems }} trigger={['click']}>
                            <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)' }} />
                        </Dropdown>
                    </Space>
                </div>
            </Header>

            <Layout>
                <Sider
                    width={260}
                    theme="light"
                    className="home-sider"
                    collapsible
                    collapsed={collapsed}
                    onCollapse={(value) => setCollapsed(value)}
                    breakpoint="lg"
                    trigger={<MenuOutlined />}
                >
                    <div className="sider-content">
                        {/* Home Navigation */}
                        <div className="nav-section">
                            <div
                                className={`nav-item ${isHome ? 'active' : ''}`}
                                onClick={() => navigate(`/${workspaceId}/home`)}
                                style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}
                            >
                                <HomeOutlined style={{ fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                {!collapsed && <span>Home</span>}
                            </div>
                        </div>

                        {/* Collections Section */}
                        <div className="nav-section">
                            {!collapsed && (
                            <div className="section-header" onClick={() => toggleSection('collections')} style={{ cursor: 'pointer' }}>
                                <span>COLLECTIONS</span>
                                <DownOutlined rotate={sectionState.collections ? 0 : -90} style={{ transition: 'transform 0.3s' }} />
                            </div>
                            )}
                            {sectionState.collections && (
                                <List
                                    className="collections-nav-list"
                                    itemLayout="horizontal"
                                    dataSource={mockCollections}
                                    renderItem={(item: Collection) => (
                                        <div
                                            className="nav-item"
                                            onClick={() => navigate(`/${workspaceId}/collection/${item.id}`)}
                                            style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}
                                        >
                                            <FolderOutlined style={{ color: '#8c8c8c', fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                            {!collapsed && <span className="text-truncate">{item.name}</span>}
                                        </div>
                                    )}
                                />
                            )}
                        </div>

                        {/* Schedulers Section */}
                        <div className="nav-section">
                            {!collapsed && (
                            <div className="section-header" onClick={() => toggleSection('schedulers')} style={{ cursor: 'pointer' }}>
                                <span>SCHEDULERS</span>
                                <DownOutlined rotate={sectionState.schedulers ? 0 : -90} style={{ fontSize: '10px', transition: 'transform 0.3s' }} />
                            </div>
                            )}
                            {sectionState.schedulers && (
                                <>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <ThunderboltOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>Automation Actions</span>}
                                    </div>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <MailOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>Emails</span>}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Embedded BI Section */}
                        <div className="nav-section">
                            {!collapsed && (
                            <div className="section-header" onClick={() => toggleSection('embeddedBi')} style={{ cursor: 'pointer' }}>
                                <span>EMBEDDED BI</span>
                                <DownOutlined rotate={sectionState.embeddedBi ? 0 : -90} style={{ fontSize: '10px', transition: 'transform 0.3s' }} />
                            </div>
                            )}
                            {sectionState.embeddedBi && (
                                <>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <DashboardOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>External Dashboards</span>}
                                    </div>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <CodeOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>API Usage</span>}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Audit Logs Section */}
                        <div className="nav-section">
                             {!collapsed && (
                            <div className="section-header" onClick={() => toggleSection('auditLogs')} style={{ cursor: 'pointer' }}>
                                <span>AUDIT LOGS</span>
                                <DownOutlined rotate={sectionState.auditLogs ? 0 : -90} style={{ fontSize: '10px', transition: 'transform 0.3s' }} />
                            </div>
                             )}
                            {sectionState.auditLogs && (
                                <div
                                    className="nav-item"
                                    onClick={() => navigate(`/${workspaceId}/system-activity`)}
                                    style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}
                                >
                                    <SafetyCertificateOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                    {!collapsed && <span>System Activity</span>}
                                </div>
                            )}
                        </div>

                        {/* Admin Section */}
                        <div className="nav-section">
                            {!collapsed && (
                            <div className="section-header" onClick={() => toggleSection('admin')} style={{ cursor: 'pointer' }}>
                                <span>ADMIN</span>
                                <DownOutlined rotate={sectionState.admin ? 0 : -90} style={{ fontSize: '10px', transition: 'transform 0.3s' }} />
                            </div>
                            )}
                            {sectionState.admin && (
                                <>
                                    <div 
                                        className="nav-item"
                                        onClick={() => navigate(`/${workspaceId}/dataset`)}
                                        style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}
                                    >
                                        <TableOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>Datasets</span>}
                                    </div>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <DatabaseOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>Databases</span>}
                                    </div>
                                    <div className="nav-item" style={{ cursor: 'pointer', justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '0 8px' : '8px 16px' }}>
                                        <ApiOutlined style={{ opacity: 0.6, fontSize: '18px', marginRight: collapsed ? 0 : 12 }} />
                                        {!collapsed && <span>Integrations</span>}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Sider>

                <Content className="home-content">
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
